import { NOT_MEASURED, exactNumber } from '../../utils/format'
import type { ErrorKind } from '../../utils/format'
import { cartographerReportPath } from '../../utils/cartographerUrl'
import type {
  RetirementCandidate, RetirementOverview, RetirementReasonFilter,
} from '../../types/retirement'
import type { RetirementEpisodeStateInfo } from '../../types/retirementEpisodes'

/**
 * Presentation for the retirement candidate list (pages/RetirementPage.tsx).
 *
 * Every helper here is a wording decision, not a calculation. The server selects,
 * orders, pages and counts; it derives `daysSinceLastRun` so the client cannot
 * disagree with the threshold that selected the row; and it withholds the run
 * counts itself when the run log cannot answer. Nothing here recomputes any of it.
 *
 * Imports only utils/format (which imports only services/apiError),
 * utils/cartographerUrl (which imports nothing) and types, so it stays testable
 * in node.
 */

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

/**
 * Each signal in words. `ScheduledUnread` is deliberately NOT called "unread": the
 * workbook is attached to the delivery email and nothing records a recipient
 * reading it, so what is known is only that nobody opened it in Cartographer.
 */
const SIGNAL_LABEL: Record<string, string> = {
  Idle: 'Idle',
  Orphaned: 'Orphaned',
  Superseded: 'Superseded',
  ScheduledUnread: 'Scheduled, not opened in Cartographer',
}

/** A signal's chip label. A name this build does not know is shown as the server sent it. */
export function reasonLabel(name: string): string {
  return SIGNAL_LABEL[name] ?? name
}

/**
 * The filter chips, in the server's wire order after "All". `value` is exactly
 * what the controller accepts (NormaliseReason); "All" is null, which sends no
 * parameter at all, because the controller refuses a blank reason with a 400.
 */
export const REASON_FILTERS: readonly { value: RetirementReasonFilter | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'idle', label: SIGNAL_LABEL.Idle },
  { value: 'orphaned', label: SIGNAL_LABEL.Orphaned },
  { value: 'superseded', label: SIGNAL_LABEL.Superseded },
  { value: 'scheduledunread', label: SIGNAL_LABEL.ScheduledUnread },
]

/**
 * A `?reason=` value from the URL, checked against the controller's spellings, or
 * null. An unrecognised value becomes "All" on the client rather than a 400 that
 * would render as a failed page for what is only a stale link.
 */
export function asReasonFilter(value: string | null | undefined): RetirementReasonFilter | null {
  const normalised = value?.trim().toLowerCase()
  const match = REASON_FILTERS.find((f) => f.value !== null && f.value === normalised)
  return match?.value ?? null
}

/** Superseded is defined and served, and cannot fire: ReplacementReportId has no writer. */
export const SUPERSEDED_CAPTION = 'Superseded cannot fire yet: nothing records a replacement.'

/** The two things the signals cannot see, stated on every visit. */
export const STANDING_CAVEATS: readonly string[] = [
  'Idle means no RECORDED run: before every open of a saved report was recorded, an unparameterized report opened in '
    + 'Spyglass recorded nothing, so an idle window reaching back that far can miss its opens.',
  'Never opened in Cartographer is not unread: the workbook is attached to the email.',
]

// ---------------------------------------------------------------------------
// Hand-offs
// ---------------------------------------------------------------------------

/** Where a signal is taken up. Observatory reads; every action is somewhere else. */
export type RetirementHandoff =
  /** Another Observatory page, by route. */
  | { signal: string; kind: 'route'; to: string; label: string }
  /** A Cartographer route, rendered through CartographerLink. */
  | { signal: string; kind: 'cartographer'; path: string; label: string }
  /** Named, not linked: there is no route that would be honest. */
  | { signal: string; kind: 'text'; text: string }

/**
 * The hand-offs for one row, in the order its signals are listed.
 *
 * - Orphaned: the Governance Queue's ownership gaps, where the missing owner is the finding.
 * - ScheduledUnread: the schedule's load here, and Town Crier in Cartographer, which
 *   owns pausing or editing the schedule.
 * - Idle: the report itself, in Cartographer — its own page, `/reports/{id}` (W3-1), where
 *   its owner opens and keeps it. Before that route existed this was TEXT, because a link
 *   would have been a fake affordance landing on a list.
 * - Superseded: nothing to hand to; the caption says why.
 */
export function handoffsFor(reasons: readonly string[], reportId: string): RetirementHandoff[] {
  const handoffs: RetirementHandoff[] = []
  for (const signal of reasons) {
    if (signal === 'Orphaned') {
      handoffs.push({ signal, kind: 'route', to: '/governance-queue?finding=ownership', label: 'Governance Queue: ownership gaps' })
    } else if (signal === 'ScheduledUnread') {
      handoffs.push({ signal, kind: 'route', to: '/schedules-load', label: 'Schedule load' })
      handoffs.push({ signal, kind: 'cartographer', path: '/schedules', label: 'Town Crier' })
    } else if (signal === 'Idle') {
      handoffs.push({ signal, kind: 'cartographer', path: cartographerReportPath(reportId), label: 'This report' })
    }
  }
  return handoffs
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/**
 * An offset-less timestamp read as UTC.
 *
 * EF reads `datetime2` as DateTimeKind.Unspecified, so the wire carries no `Z`, and
 * a browser parses an offset-less date-time as LOCAL time: hours out, and on the
 * wrong day for a run near midnight. A value that already carries `Z` or an offset
 * is returned unchanged; a date with no time part is too (ECMAScript already
 * parses that form as UTC).
 */
export function asUtc(iso: string): string {
  if (!iso.includes('T')) return iso
  return /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`
}

function dayOf(iso: string): string | null {
  const parsed = new Date(asUtc(iso))
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString()
}

/** The full instant, for a tooltip beside a date. */
export function instantLabel(iso: string | null): string | null {
  if (iso == null) return null
  const parsed = new Date(asUtc(iso))
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString()
}

/**
 * The Last run cell. Null is "Never run", a finding in its own right and not a
 * dash: the server documents null as "has never recorded one", and never-run and
 * long-idle are different conversations.
 */
export function lastRunText(lastRunAt: string | null): string {
  if (lastRunAt == null) return 'Never run'
  return dayOf(lastRunAt) ?? NOT_MEASURED
}

/** The Days since cell. Null (never run) is the dash; the Last run cell says why. */
export function daysSinceText(days: number | null): string {
  return days == null ? NOT_MEASURED : exactNumber(days)
}

/**
 * The Workspace cell. A null workspace is a PERSONAL report, not an unfiled
 * legacy one: that is how a personal report is stored, so its isPersonalWorkspace
 * is false. A workspace that is served by id but not by name is the dash.
 */
export function workspaceText(candidate: Pick<RetirementCandidate, 'workspaceId' | 'workspaceName'>): string {
  if (candidate.workspaceId == null) return 'Personal (not filed)'
  return candidate.workspaceName ?? NOT_MEASURED
}

/**
 * The Owner cell and its tooltip.
 *
 * No owner id at all is the certain ownership gap, and says so. An owner with no
 * stamped name is the dash with the id behind it: ids are opaque pairwise `sub`
 * values and nothing resolves one to a person, so the id is never dressed up as a name.
 */
export function ownerText(
  candidate: Pick<RetirementCandidate, 'ownerUserId' | 'ownerDisplayName'>,
): { text: string; tooltip: string | null } {
  if (candidate.ownerUserId == null) {
    return { text: 'No owner', tooltip: 'The report has no owner at all: the certain ownership gap' }
  }
  const name = candidate.ownerDisplayName?.trim()
  return name
    ? { text: name, tooltip: `Owner id: ${candidate.ownerUserId}` }
    : { text: NOT_MEASURED, tooltip: `Owner id: ${candidate.ownerUserId}. No name was stamped for this owner` }
}

/** The Last run by tooltip when the server withheld the name and said nothing of why. */
export const LAST_RUN_BY_WITHHELD_HINT =
  'Withheld: this report is not filed in a shared workspace, so who ran it is not named'

/** The Last run by tooltip when no recorded run named anyone. */
export const LAST_RUN_BY_UNRECORDED_HINT =
  'No recorded run named who ran it: runs recorded before Cartographer stamped the runner name nobody'

/**
 * The Last run by cell and its tooltip (Cartographer doc 15 §11, R4).
 *
 * A served name is printed as it was stamped. Withheld and never recorded are both
 * the dash, and the tooltip says which: withheld is not absent. A report never run
 * has no runner to name, so it is the dash with no tooltip — the Last run cell says
 * why. The id is never shown: the server serves names only.
 */
export function lastRunByText(
  candidate: Pick<RetirementCandidate, 'lastRunAt' | 'lastRunBy' | 'lastRunByWithheld' | 'lastRunByWithheldReason'>,
): { text: string; tooltip: string | null } {
  const name = candidate.lastRunBy?.trim()
  if (name) return { text: name, tooltip: null }

  if (candidate.lastRunByWithheld) {
    return {
      text: NOT_MEASURED,
      tooltip: candidate.lastRunByWithheldReason?.trim() || LAST_RUN_BY_WITHHELD_HINT,
    }
  }

  if (candidate.lastRunAt == null) return { text: NOT_MEASURED, tooltip: null }
  return { text: NOT_MEASURED, tooltip: LAST_RUN_BY_UNRECORDED_HINT }
}

/**
 * A run count inside the unread window. Null is the dash, never 0: the server
 * withholds the count when the run log does not reach back across the window,
 * and "delivered 0 times" and "the record was trimmed" are opposite findings.
 */
export function runCountText(count: number | null): string {
  return count == null ? NOT_MEASURED : exactNumber(count)
}

/** The tooltip on a withheld run count. */
export const RUN_COUNT_WITHHELD_HINT =
  'Log trimmed: the run log does not reach back across the whole window, so this is not known'

/**
 * The header tooltips on the two run-count columns. Both count recorded runs of ANY
 * outcome (decision R2, 2026-10-01): a delivery that failed is a scheduled run and an
 * open that failed an interactive one. So neither says "delivered" or "opened", which
 * a failed run is not; the served caption (runCountsCaption) says the rest.
 */
export function scheduledRunsHint(unreadThresholdDays: number): string {
  return `Scheduled runs recorded in the last ${exactNumber(unreadThresholdDays)} days, whatever their outcome: `
    + 'a delivery that failed counts'
}

/** See scheduledRunsHint. */
export function interactiveRunsHint(unreadThresholdDays: number): string {
  return `Interactive runs recorded in Cartographer in the last ${exactNumber(unreadThresholdDays)} days, `
    + 'whatever their outcome. Zero never means unread: the workbook is attached to the email'
}

/**
 * The server's caption on what the two run counts count, or null when it served none
 * (an API that predates it). Served, not written here: it changes with what the run
 * log records.
 */
export function runCountsCaption(overview: Pick<RetirementOverview, 'runCountsCaption'>): string | null {
  const caption = overview.runCountsCaption?.trim()
  return caption ? caption : null
}

/**
 * The report's episode, or null when it has none. The by-report response OMITS a
 * report with no episode, so a miss means "never on the consent clock".
 */
export function episodeFor(
  states: readonly RetirementEpisodeStateInfo[] | undefined,
  reportId: string,
): RetirementEpisodeStateInfo | null {
  return states?.find((e) => e.reportId === reportId) ?? null
}

// ---------------------------------------------------------------------------
// Page-level text
// ---------------------------------------------------------------------------

/**
 * The one banner the page shows when ScheduledUnread was not evaluated, or null
 * when it was. When the log cannot cover the window, the absence of that signal on
 * every row is not a finding, so the page says so once, at the top.
 */
export function runLogBannerText(
  overview: Pick<RetirementOverview, 'runLogCoversUnreadWindow' | 'runLogReachesBackTo'>,
): string | null {
  if (overview.runLogCoversUnreadWindow) return null
  if (overview.runLogReachesBackTo == null) {
    return 'Scheduled-but-unread was not evaluated: the run log is empty, so no run has ever been recorded'
  }
  return `Scheduled-but-unread was not evaluated: the run log reaches back only to ${
    instantLabel(overview.runLogReachesBackTo) ?? overview.runLogReachesBackTo}`
}

/** What counts as a recorded run for Idle, in the words both retirement surfaces use. */
export const IDLE_RUN_MEANING =
  'a failed open, export or delivery counts as a run, except a delivery whose query failed'

/** The header line: the served count, of the served denominator. */
export function candidateHeadline(
  overview: Pick<RetirementOverview, 'totalCount' | 'reportsExamined'>,
  reason: RetirementReasonFilter | null,
): string {
  const of = `of ${exactNumber(overview.reportsExamined)} ${overview.reportsExamined === 1 ? 'report' : 'reports'} examined`
  const noun = overview.totalCount === 1 ? 'candidate' : 'candidates'
  if (reason == null) return `${exactNumber(overview.totalCount)} ${noun} ${of}`
  const label = REASON_FILTERS.find((f) => f.value === reason)?.label ?? reason
  return `${exactNumber(overview.totalCount)} ${noun} flagged ${label} ${of}`
}

/**
 * The thresholds, in the server's numbers, never this client's. A recorded run, for Idle, is any run Cartographer
 * records, a FAILED one included (a failed open is still somebody wanting the report) — except a scheduled delivery
 * whose query failed, which moves nothing, as it recorded nothing before failed runs were recorded. That is
 * `ReportStore.StampsLastRunAt`, and the hint says so because "no recorded run" alone reads as "nothing succeeded".
 */
export function thresholdText(
  overview: Pick<RetirementOverview, 'idleThresholdDays' | 'unreadThresholdDays'>,
): string {
  return `Idle: no recorded run for ${exactNumber(overview.idleThresholdDays)} days, in a report at least that old — `
    + `${IDLE_RUN_MEANING}. `
    + `Scheduled, not opened in Cartographer: delivered on a schedule and never opened there in the last `
    + `${exactNumber(overview.unreadThresholdDays)} days.`
}

/**
 * The empty list, by what the emptiness means. Never "all clear" where the
 * signal could not be read: ScheduledUnread with an uncovered log is "not
 * evaluated", and Superseded is "cannot fire yet".
 */
export function emptyCandidatesText(
  overview: Pick<RetirementOverview, 'reportsExamined' | 'runLogCoversUnreadWindow'>,
  reason: RetirementReasonFilter | null,
): { title: string; message: string } {
  if (overview.reportsExamined === 0) {
    return {
      title: 'No reports to examine',
      message: 'No Active or AttentionRequired report exists, so there is nothing a signal could be read over.',
    }
  }

  const examined = `${exactNumber(overview.reportsExamined)} ${overview.reportsExamined === 1 ? 'report' : 'reports'} examined`

  if (reason === 'scheduledunread' && !overview.runLogCoversUnreadWindow) {
    return {
      title: 'Not evaluated',
      message: 'This signal was not evaluated, because the run log does not reach back across its window. '
        + 'An empty list here is the signal being unavailable, not a finding that no schedule goes unopened.',
    }
  }

  if (reason === 'superseded') {
    return {
      title: 'No superseded reports',
      message: `None of the ${examined} is superseded, and none can be yet: nothing records a replacement.`,
    }
  }

  if (reason != null) {
    const label = REASON_FILTERS.find((f) => f.value === reason)?.label ?? reason
    return { title: `No candidates flagged ${label}`, message: `No report among the ${examined} carries this signal.` }
  }

  return {
    title: 'No retirement candidates',
    message: overview.runLogCoversUnreadWindow
      ? `No signal fired for any of the ${examined}.`
      : `No signal fired for any of the ${examined}, and scheduled-but-unread was not evaluated at all.`,
  }
}

/** The failed list's title, by kind. The body always adds that nothing is known. */
export function candidateErrorTitle(kind: ErrorKind): string {
  if (kind === 'unauthorized') return 'Not signed in'
  if (kind === 'network') return 'The API could not be reached'
  return 'Could not load retirement candidates'
}
